/* eslint-disable @typescript-eslint/no-explicit-any */
import { useContext, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Autocomplete from '@mui/material/Autocomplete';
import TextField, { TextFieldProps } from '@mui/material/TextField';
import { Building02 } from '../../../assets';
import { Store, User, Franchise } from '../../../app/services/api/generated';
import { UserContext } from '../../../components/shared/useUser';
import useNswagClient from '../../../hooks/api/useNswagClient';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Alert, Button } from '@mui/material';
import useLogError from '../../../hooks/useLogError';
import useLocalStorage from '../../../hooks/useLocalStorage';

const textClamp = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: '-webkit-box',
  WebkitLineClamp: '1',
  WebkitBoxOrient: 'vertical',
};

const InitialStoreSelector = ({ onGoBack }: { onGoBack?: () => void }) => {
  const { getStores, getCurrentUser } = useNswagClient();

  // const [storedValue, setValue] = useLocalStorage('token', null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [flowCompleted, setFlowCompleted] = useState<boolean>(false);
  const [showFranchiseDropdown, setShowFranchiseDropdown] = useState<boolean>(false);
  const [showStoreDropdown, setShowStoreDropdown] = useState<boolean>(false);
  const [storedValue] = useLocalStorage('token', null);

  const navigate = useNavigate();
  const { t } = useTranslation('authentication');
  const {
    setUserContext,
    setSelectedStore,
    user,
    token,
    selectedFranchise,
    setFranchises,
    setUserStores,
    setSelectedFranchise,
    franchises,
  } = useContext(UserContext);
  
  const { logError } = useLogError();
  const [loading, setLoading] = useState<boolean>(false);


  useEffect(() => {
    retrieveUser();
  }, []);
  
  useEffect(() => {
    if (flowCompleted ) {
      navigate('/');
    }
  }, [flowCompleted, user, selectedFranchise, token]);

  const handleSingleStoreUser = (stores: Store[], franchises: Franchise[]) => {
    if (franchises.length === 1) {
      const singleFranchise = franchises[0];
      setSelectedFranchise(singleFranchise);
      setShowFranchiseDropdown(false);

      const franchiseStores = stores.filter(s => s.franchiseId === singleFranchise.franchiseId);

      if (franchiseStores.length === 1) {
        const singleStore = franchiseStores[0];
        setSelectedStore({ ...singleStore, franchiseName: singleFranchise.franchiseName });
        setShowStoreDropdown(false);
        setFlowCompleted(true);
      } 
      else {
        setShowStoreDropdown(true);
      }
    } 
    else if (franchises.length > 1) {
      setShowFranchiseDropdown(true);
    } 
  };

  const retrieveUser = () => {
    setLoading(true);
    getCurrentUser().then((result) => {
      if (!result?.data?.persona) {
        setErrorMessage(t('errorMessages.signInFailed'));
        const language = localStorage.getItem('language');
        localStorage.clear();
        if (language) {
          localStorage.setItem('language', language);
          localStorage.setItem('i18nextLng', language);
        }
      } else if (result?.data?.stores) {
        const user: User = {
          ...result.data,
          stores: result.data.stores,
        };
        setUserContext(user, token ?? '');
        setUserStores(result.data.stores);
        setFranchises(result.data.franchises || []);

        handleSingleStoreUser(result.data.stores, result.data.franchises || []);
      } else {
        setErrorMessage(t('errorMessages.noStoresAssigned'));
        const language = localStorage.getItem('language');
        localStorage.clear();
        if (language) {
          localStorage.setItem('language', language);
          localStorage.setItem('i18nextLng', language);
        }
      }
    })
      .catch((error) => {
        const language = localStorage.getItem('language');
        localStorage.clear();
        if (language) {
          localStorage.setItem('language', language);
          localStorage.setItem('i18nextLng', language);
        }
        logError(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleChangeStore = (newValue: Store) => {
    if (newValue) {
      setSelectedStore({ ...newValue, franchiseName: selectedFranchise?.franchiseName });
      setFlowCompleted(true);
    }
  };

  const handleChangeFranchise = (newValue: Franchise) => {
    if (newValue) {
      setSelectedFranchise(newValue);
      setSelectedStore(null);

      const franchiseStores = (user?.allAccessibleStores ?? user?.stores ?? []).filter(s => s.franchiseId === newValue.franchiseId);
      if (franchiseStores.length === 1) {
        handleChangeStore(franchiseStores[0]);
        setShowStoreDropdown(false);
      } else {
        setShowStoreDropdown(true);
      }
    }
  };

  useEffect(() => {
    if (selectedFranchise) {
      const franchiseStores = (user?.allAccessibleStores ?? user?.stores ?? []).filter(s => s.franchiseId === selectedFranchise.franchiseId);
      setShowStoreDropdown(franchiseStores.length > 0);

      if (franchiseStores.length === 1) {
        handleChangeStore(franchiseStores[0]);
      }
    }
  }, [selectedFranchise]);


  if (errorMessage) {
    return <Box
      sx={{ position: 'absolute', top: '35%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
      <Alert severity="error">{errorMessage}</Alert>
      <Button
        size="md"
        variant="secondary"
        onClick={() => (onGoBack ? onGoBack() : navigate('/login'))}
      >{t('goBack')}</Button>
    </Box>;
  }

  return (
    <Box sx={{
      position: 'absolute',
      top: '35%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      width: '360px',
    }}>
      {showFranchiseDropdown && (
        <Autocomplete
          onChange={(_, newValue: Franchise | null) => {
            if (newValue) handleChangeFranchise(newValue);
          }}
          id="select-franchise"
          options={franchises ?? []}
          sx={{ width: '100%', overflow: 'auto', pt: 4 }}
          getOptionLabel={(option: Franchise) => option.franchiseName ?? ''}
          filterOptions={(options: Franchise[], state) =>
            options.filter(option =>
              option.franchiseName?.toLowerCase().includes(state.inputValue.toLowerCase()),
            )
          }
          renderOption={(props, option) => (
            <Box
              component="li"
              sx={{ '& > img': { mr: 2, flexShrink: 0 }, gap: 2 }}
              {...props}
              key={option.franchiseId}
            >
              <Building02 style={{ minWidth: '24px' }} />
              <Typography variant="textSM"
                sx={{ ...textClamp }}>
                {option.franchiseName}
              </Typography>
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              placeholder={t('selectFranchise')}
              {...(params as TextFieldProps)}
            />
          )}
          value={selectedFranchise}
        />
      )}

      {showStoreDropdown && (
        <Autocomplete
          loading={loading}
          onChange={(event: any, newValue: Store | null) => {
            if (newValue)
              handleChangeStore(newValue);
          }}
          id="select-store"
          options={(user?.allAccessibleStores ?? user?.stores ?? []).filter(s => s.franchiseId == selectedFranchise?.franchiseId) }
          sx={{ width: '100%', overflow: 'auto', pt: 4 }}
          getOptionLabel={(option: Store) => option.storeName ?? ''}
          filterOptions={(options: Store[], state) => options.filter((option) => option.storeNumber?.startsWith(state.inputValue) || option.storeName?.toLowerCase().includes(state.inputValue.toLowerCase()))}
          renderOption={(props, option) => (
            <Box component="li"
              sx={{ '& > img': { mr: 2, flexShrink: 0 }, gap: 2 }}
              {...props}
              key={option.storeName}>
              <Building02 style={{ minWidth: '24px' }} />
              <Typography
                variant='textSM'
                sx={{
                  ...textClamp,
                }}>
                {option.storeName}
              </Typography>
            </Box>
          )}
          renderInput={(params) => {
            return <TextField placeholder={t('selectStore')}
              {...params as TextFieldProps} />;
          }}
        />
      )}
    </Box>
  );
};

export default InitialStoreSelector;
